import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { useUiStore } from "@/stores/ui.store";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { AddressMapSearch } from "@/components/AddressMapSearch";
import { Card, CardContent } from "@/components/ui/Card";
import { Typography } from "@/components/ui/Typography";

export function EditPropertyPage() {
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id as string;
  const addToast = useUiStore((s) => s.addToast);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const PROPERTY_TYPES = [
    { value: "APARTMENT", label: "Apartment" },
    { value: "INDEPENDENT_HOUSE", label: "Independent House" },
    { value: "VILLA", label: "Villa" },
    { value: "PLOT", label: "Plot" },
    { value: "COMMERCIAL", label: "Commercial" },
    { value: "OTHER", label: "Other" },
  ] as const;

  const [form, setForm] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    propertyType: "OTHER" as string,
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined,
  });

  useEffect(() => {
    async function load() {
      try {
        const prop = await api.get<{
          name: string;
          address: string;
          city: string | null;
          state: string | null;
          pincode: string | null;
          propertyType: string | null;
          latitude: number | null;
          longitude: number | null;
        }>(`/properties/${id}`);
        setForm({
          name: prop.name,
          address: prop.address,
          city: prop.city ?? "",
          state: prop.state ?? "",
          pincode: prop.pincode ?? "",
          propertyType: prop.propertyType ?? "OTHER",
          latitude: prop.latitude ?? undefined,
          longitude: prop.longitude ?? undefined,
        });
      } catch {
        addToast({ type: "error", message: "Failed to load property" });
        navigate("/properties");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, navigate, addToast]);

  function update(field: string, value: string | number | undefined) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = "Property name is required";
    if (!form.address.trim()) newErrors.address = "Address is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/properties/${id}`, {
        name: form.name.trim(),
        address: form.address.trim(),
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        pincode: form.pincode.trim() || null,
        propertyType: form.propertyType !== "OTHER" ? form.propertyType : null,
        latitude: form.latitude,
        longitude: form.longitude,
      });
      addToast({ type: "success", message: "Property updated" });
      navigate(`/property/${id}`);
    } catch (err) {
      addToast({
        type: "error",
        message: err instanceof ApiError ? err.message : "Failed to update",
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
        <div className="h-96 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <Link
          to={`/property/${id}`}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-2"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Back to property
        </Link>
        <Typography variant="h1">Edit Property</Typography>
        <p className="mt-1 text-sm text-slate-500">
          Update property details. To add or edit units and tenants, go to the property detail page.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardContent className="space-y-4">
            <Typography variant="h2">Property details</Typography>
            <Input label="Property name" value={form.name} onChange={(e) => update("name", e.target.value)} error={errors.name} required />
            <AddressMapSearch
              label="Address"
              value={form.address}
              latitude={form.latitude}
              longitude={form.longitude}
              onChange={(r) => {
                update("address", r.address);
                update("latitude", r.latitude);
                update("longitude", r.longitude);
              }}
              error={errors.address}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Input label="City" value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="e.g. Bangalore" />
              <Input label="State" value={form.state} onChange={(e) => update("state", e.target.value)} placeholder="e.g. Karnataka" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input label="Pincode" value={form.pincode} onChange={(e) => update("pincode", e.target.value)} placeholder="e.g. 560001" />
              <Select
                label="Property type"
                options={PROPERTY_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                value={form.propertyType}
                onChange={(e) => update("propertyType", e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Link to={`/property/${id}`}>
            <Button variant="secondary" type="button">Cancel</Button>
          </Link>
          <Button type="submit" isLoading={submitting}>Save changes</Button>
        </div>
      </form>
    </div>
  );
}
